import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, Shield, Warning } from "@phosphor-icons/react";

const DEFAULT_CTX = {
  first_name: "Marco", country: "Italy", company: "Atlas Freight",
  corridor_hook: "Trieste-Koper Adriatic project cargo",
  slot_date: "Thu Mar 4", slot_time_cet: "10:00",
  meeting_link: "https://cal.epas.one/rok/20min",
  reschedule_link: "https://cal.epas.one/rok/reschedule",
  fresh_angle: "the new Trieste rail terminal opens in April.",
  event_name: "EPAS Adriatic Roundtable", event_date: "Sep 24, 2026",
  event_city: "Koper", event_focus: "corridor volumes and partner introductions",
  attendee_persona: "founders and operations directors",
  rsvp_link: "https://epasone.myeuropacific.com/portal/events/adriatic",
  applicant_company: "Atlas Freight Italia", applicant_city: "Trieste",
  applicant_country: "Italy", application_id: "153932",
  referee_name: "Andrea Bianchi", reply_by_date: "Feb 20",
  set_password_url: "https://epasone.myeuropacific.com/portal/set-password?token=xyz",
  profile_completion_percent: 40,
  match_1_line: "Bosphorus Logistics (Turkiye) — Adriatic + Middle East overlap.",
  match_2_line: "Adria Cargo Partners (Slovenia) — Koper co-loading.",
  match_3_line: "Hanseatic Freight GmbH (Germany) — feeder.",
  new_member_company: "Bosphorus Logistics", new_member_country: "Turkiye",
  new_member_city: "Istanbul",
  new_member_corridor_focus: "Adriatic-Turkiye rail-sea hybrid corridor specialist",
  new_member_modes: "sea, rail, project cargo",
  new_member_cargo_types: "heavy-lift, DGR class 3, breakbulk",
  new_member_recent_example: "12 heavy-lift crates Koper→Istanbul, closed with Adria Cargo Partners in Feb 2026",
  new_member_profile_url: "https://epasone.myeuropacific.com/portal/directory/bosphorus",
  attendee_first_names: "Marco, Andrea, Nikolaj, Rok",
  confirmed_attendees_list: "· Marco Bianchi — Atlas Freight (Italy)\n· Andrea Rossi — Trieste Cargo Partners (Italy)\n· Nikolaj Petrov — Bosphorus Logistics (Turkiye)\n· Rok Kobal — Europacific (Slovenia)",
  month_name: "January", new_members_count: 2, new_countries_count: 2,
  new_members_list: "- Bosphorus Logistics (Turkiye)\n- Levant Cargo (UAE)",
  story_line_1: "First joint Adriatic-Med project cargo shipment closed.",
  story_line_2: "Koper rail terminal opens April 2026.",
  upcoming_events_list: "- EPAS Adriatic Roundtable · Sep 24 · Koper",
  shipment_reference: "EPAS-2026-0142",
  counterparty_company: "GLS Baltic", amount_disputed: "EUR 12400",
  incident_status: "under review", next_review_date: "Feb 18",
  incident_url: "https://epasone.myeuropacific.com/portal/payments/inc-0142",
  event_time_cet: "10:00", event_location: "Port of Koper",
  access_channel: "Zoom", access_link: "https://zoom.us/j/1234567890",
  cancel_link: "https://epasone.myeuropacific.com/portal/events/adriatic/cancel",
  renewal_date: "Mar 15, 2027", days_until_renewal: 30,
  renewal_amount: "EUR 2,500", renewal_url: "https://epasone.myeuropacific.com/portal/renewal",
};

const CAT_LABEL = {
  sales: "Sales / Acquisition",
  onboarding: "Onboarding",
  portal_ops: "Portal & Ongoing",
};

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ctx, setCtx] = useState(DEFAULT_CTX);
  const [preview, setPreview] = useState(null);
  const [guarding, setGuarding] = useState(false);

  useEffect(() => {
    api.get("/templates")
      .then(({ data }) => {
        setTemplates(data.templates || []);
        if (data.templates?.length) setSelected(data.templates[0].key);
      })
      .catch(() => toast.error("Could not load templates"));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of templates) (g[t.category] = g[t.category] || []).push(t);
    return g;
  }, [templates]);

  const current = templates.find((t) => t.key === selected);

  const generate = async () => {
    if (!selected) return;
    setGuarding(true);
    try {
      // Only pass vars actually needed
      const required = (current?.required_vars || []);
      const payload = {};
      required.forEach((k) => { if (ctx[k] !== undefined) payload[k] = ctx[k]; });
      // Include all common vars just in case
      const full = { ...ctx };
      const { data } = await api.post(`/templates/${selected}/preview`, full);
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      setPreview(null);
    } finally {
      setGuarding(false);
    }
  };

  useEffect(() => { if (selected) generate(); /* eslint-disable-next-line */ }, [selected]);

  return (
    <div className="max-w-7xl mx-auto py-8 px-6" data-testid="admin-templates">
      <header className="mb-8">
        <div className="overline mb-2">Admin · Automation</div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
          <EnvelopeSimple size={26} weight="duotone" className="text-ember" /> Email Template Library
        </h1>
        <p className="text-navy/70 text-sm mt-2 max-w-2xl">
          19 templates covering full member lifecycle: sales, onboarding, and ongoing portal comms. Every render
          is checked against the Template-Integrity Guard before dispatch.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside className="space-y-6" data-testid="templates-sidebar">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="overline mb-2">{CAT_LABEL[cat] || cat}</div>
              <div className="border border-black/10 bg-white">
                {list.map((t) => (
                  <button key={t.key} onClick={() => setSelected(t.key)}
                    data-testid={`template-item-${t.key}`}
                    className={`w-full text-left px-3 py-2.5 text-sm border-b border-black/5 last:border-b-0 ${selected === t.key ? "bg-navy text-ember" : "hover:bg-black/5 text-navy"}`}>
                    <div className="font-medium">{t.label}</div>
                    <div className={`text-[10px] uppercase tracking-widest mt-0.5 ${selected === t.key ? "text-ember/70" : "text-navy/40"}`}>{t.channel} · {t.key}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <main data-testid="templates-preview">
          {current && (
            <div className="border border-black/10 bg-white">
              <div className="border-b border-black/10 px-5 py-4">
                <div className="overline">{CAT_LABEL[current.category]} · {current.channel}</div>
                <h2 className="font-display font-bold text-xl text-navy mt-1">{current.label}</h2>
                <p className="text-sm text-navy/70 mt-1">{current.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-black/10">
                <div className="p-5">
                  <div className="overline mb-3">Merge variables</div>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2">
                    {(current.required_vars || []).map((k) => (
                      <label key={k} className="block text-xs">
                        <div className="text-navy/60 uppercase tracking-widest text-[10px]">{k}</div>
                        <input value={ctx[k] ?? ""} onChange={(e) => setCtx({ ...ctx, [k]: e.target.value })}
                          data-testid={`ctx-${k}`}
                          className="w-full border border-black/10 px-2 py-1.5 mt-0.5 text-sm" />
                      </label>
                    ))}
                    {!current.required_vars?.length && (
                      <div className="text-xs text-navy/50">This template has no required variables.</div>
                    )}
                  </div>
                  <button onClick={generate} disabled={guarding} data-testid="template-render"
                    className="mt-4 bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy/90 disabled:opacity-50 w-full">
                    {guarding ? "Rendering…" : "Render + check"}
                  </button>
                </div>

                <div className="p-5 bg-[#FAFAFA]">
                  <div className="overline mb-3">Rendered output</div>
                  {preview ? (
                    <>
                      {preview.subject && (
                        <div className="mb-3">
                          <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1">Subject</div>
                          <div className="bg-white border border-black/10 px-3 py-2 text-sm font-medium text-navy" data-testid="preview-subject">{preview.subject}</div>
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1">Body</div>
                      <pre className="bg-white border border-black/10 px-3 py-2 text-xs text-navy whitespace-pre-wrap font-sans max-h-[340px] overflow-y-auto" data-testid="preview-body">{preview.body}</pre>

                      <div className={`mt-4 p-3 border ${preview.guard?.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="preview-guard">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          {preview.guard?.ok
                            ? <><Shield size={16} className="text-emerald-700" /><span className="text-emerald-800">Guard passed</span></>
                            : <><Warning size={16} className="text-red-700" /><span className="text-red-800">{preview.guard?.violations?.length} violation(s)</span></>}
                        </div>
                        {!preview.guard?.ok && (
                          <ul className="mt-2 text-xs text-red-800 space-y-1">
                            {preview.guard.violations.map((v, i) => (
                              <li key={i}>· <span className="font-medium">{v.rule}</span> — {v.hint} <span className="text-red-600">[{v.excerpt}]</span></li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-navy/50">Fill in variables and click "Render + check".</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
